import React, { useState, useEffect } from 'react';
import { useUserManagement } from '../hooks/useUserManagement';
import UserManagementPopup from './UserManagementPopup';
import AddUserModal from './AddUserModal';
import UserActivationWizard from './UserActivationWizard';
import { 
  formatLicenseCount, 
  getLicenseStatusColor, 
  getBillingModelDescription 
} from '../utils/billingHelpers';

const UserManagement = ({ subscriptionData, onRefresh, showMessage }) => {
const [showAddUserModal, setShowAddUserModal] = useState(false);
const [addingUserName, setAddingUserName] = useState(null);
const [deletingUserName, setDeletingUserName] = useState(null);
const [usersData, setUsersData] = useState(null);
const [searchQuery, setSearchQuery] = useState('');
const [selectedUser, setSelectedUser] = useState(null);
const [showUserPopup, setShowUserPopup] = useState(false);
const [showActivationWizard, setShowActivationWizard] = useState(false);
const [newlyCreatedUser, setNewlyCreatedUser] = useState(null);
  
const { 
  loading, 
  error,
  getUsers, 
  addUser, 
  deleteUser, 
  updateUserLicense, 
  assignReports, 
  removeReportAssignments,
  getLicenseInfo, 
  currentAdminId 
} = useUserManagement(
  subscriptionData,
  null // Remove success messages
);

  // Load users data
  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsersData(data);
    } catch (err) {
      showMessage?.(`Failed to load users: ${err.message}`, 'error');
    }
  };

  // Load users when component mounts or subscription data changes
  useEffect(() => {
    if (subscriptionData && currentAdminId) {
      // Use the users from subscription data if available, otherwise load separately
      if (subscriptionData.users && subscriptionData.users.length > 0) {
        setUsersData(subscriptionData.users);
      } else {
        loadUsers();
      }
    }
  }, [subscriptionData, currentAdminId]);

  // Handle adding new user
  const handleAddUser = async (email, fullName) => {
    // Store user name for progress display
    setAddingUserName(fullName);
    
    try {
      const result = await addUser(email, fullName);
      
      // If successful, close the add modal
      setShowAddUserModal(false);
      
      // Store the newly created user data and show activation wizard
      setNewlyCreatedUser({
        userID: result.userID || result.UserID,
        email: result.email || result.Email || email,
        fullName: result.fullName || result.FullName || fullName,
        hasActiveLicense: result.hasActiveLicense || result.HasActiveLicense || false
      });
      setShowActivationWizard(true);
      
    } catch (err) {
      showMessage?.(`Failed to add user: ${err.message}`, 'error');
      // Don't close modal on error so user can retry
    } finally {
      // Clear the adding user name after a delay
      setTimeout(() => setAddingUserName(null), 1500);
    }
  };

  // Handle activation wizard completion
  const handleActivateAndAssign = async (options) => {
    if (!newlyCreatedUser) return;

    const userId = newlyCreatedUser.userID;

    try {
      // Step 1: Activate license if requested and available
      if (options.activateLicense) {
        await updateUserLicense(userId, true);
      }

      // Step 2: Assign reports if any were selected
      if (options.reportIds && options.reportIds.length > 0) {
        await assignReports(userId, options.reportIds);
      }

      // Close wizard and refresh data
      setShowActivationWizard(false);
      setNewlyCreatedUser(null);
      loadUsers();
      onRefresh?.();

    } catch (err) {
      showMessage?.(`Error during setup: ${err.message}`, 'error');
      // Keep wizard open on error so user can retry
    }
  };

  // Handle skipping activation wizard
  const handleSkipActivation = () => {
    setShowActivationWizard(false);
    setNewlyCreatedUser(null);
    // No success message when skipping
    loadUsers();
    onRefresh?.();
  };

  // Handle opening user management popup
  const handleOpenUserPopup = async (user) => {
    setSelectedUser(user);
    setShowUserPopup(true);
  };

  // Handle closing user management popup
  const handleCloseUserPopup = () => {
    setSelectedUser(null);
    setShowUserPopup(false);
  };

  // Handle license update from popup
  const handleUpdateLicense = async (user, newLicenseStatus) => {
    const userId = user.userID || user.UserID;
    
    try {
      // Optimistically update the local state first for immediate UI feedback
      const updatedUsers = displayUsers.map(u => {
        const uId = u.userID || u.UserID;
        if (uId === userId) {
          return {
            ...u,
            hasActiveLicense: newLicenseStatus,
            HasActiveLicense: newLicenseStatus
          };
        }
        return u;
      });
      
      // Update local state immediately
      if (Array.isArray(usersData)) {
        setUsersData(updatedUsers);
      } else if (usersData && usersData.users) {
        setUsersData({
          ...usersData,
          users: updatedUsers
        });
      }

      // Update selected user for popup
      setSelectedUser(prev => prev ? {
        ...prev,
        hasActiveLicense: newLicenseStatus,
        HasActiveLicense: newLicenseStatus
      } : null);

      // Then make the API call
      await updateUserLicense(userId, newLicenseStatus);
      
      // Refresh data after successful update
      loadUsers();
      onRefresh?.();
    } catch (err) {
      // If the API call fails, reload the data to revert the optimistic update
      loadUsers();
      showMessage?.(`Failed to update license: ${err.message}`, 'error');
    }
  };

  // Handle user deletion from popup
  const handleDeleteUser = async (user) => {
    const userId = user.userID || user.UserID;
    const userEmail = user.email || user.Email;
    const userName = user.fullName || user.FullName || 'Unknown User';

    // Prevent deleting the subscription's original/beneficiary user
    const normUserId = userId != null ? String(userId) : null;
    const normOriginalId = originalAdminId != null ? String(originalAdminId) : null;
    const normUserEmail = userEmail ? String(userEmail).trim().toLowerCase() : null;
    const normOriginalEmail = originalAdminEmail ? String(originalAdminEmail).trim().toLowerCase() : null;

    if ((normOriginalId && normUserId === normOriginalId) || (normOriginalEmail && normUserEmail === normOriginalEmail)) {
      showMessage?.('You cannot delete the subscription owner (beneficiary).', 'error');
      return;
    }

    // Prevent deleting users who are also admins
    const isAdmin = adminList.some(admin => {
      const adminEmailNorm = (admin.email || admin.Email || '').trim().toLowerCase();
      const adminIdNorm = admin.adminID || admin.AdminID;
      return adminEmailNorm === normUserEmail || String(adminIdNorm) === normUserId;
    });

    if (isAdmin) {
      showMessage?.('You cannot delete a user who is also an administrator. Remove them from the Admins tab first.', 'error');
      return;
    }

    // Store user name for progress display
    setDeletingUserName(userName);

    try {
      await deleteUser(userId, userEmail);

      // Refresh data after successful deletion
      loadUsers();
      onRefresh?.();
    } catch (err) {
      showMessage?.(`Failed to delete user: ${err.message}`, 'error');
    } finally {
      // Clear the deleting user name after a delay
      setTimeout(() => setDeletingUserName(null), 1500);
    }
  };

  // Handle report assignment
  const handleAssignReports = async (userId, reportIds) => {
    try {
      await assignReports(userId, reportIds);
      // Update the selected user with new assignments (optimistic update)
      const newAssignments = reportIds.map(reportId => {
        const report = availableReports.find(r => (r.reportID || r.ReportID) === reportId);
        return {
          reportID: reportId,
          ReportID: reportId,
          reportName: report?.reportName || report?.ReportName,
          ReportName: report?.reportName || report?.ReportName,
          uploadDate: report?.uploadDate || report?.UploadDate,
          UploadDate: report?.uploadDate || report?.UploadDate
        };
      });
      
      setSelectedUser(prev => prev ? {
        ...prev,
        assignedReports: [...(prev.assignedReports || []), ...newAssignments],
        AssignedReports: [...(prev.AssignedReports || []), ...newAssignments]
      } : null);
      
      // Refresh data
      loadUsers();
      onRefresh?.();
    } catch (err) {
      showMessage?.(`Failed to assign reports: ${err.message}`, 'error');
    }
  };

  // Handle report removal
  const handleRemoveReports = async (userId, reportIds) => {
    try {
      await removeReportAssignments(userId, reportIds);
      // Update the selected user by removing assignments (optimistic update)
      setSelectedUser(prev => prev ? {
        ...prev,
        assignedReports: (prev.assignedReports || []).filter(report => 
          !reportIds.includes(report.reportID || report.ReportID)
        ),
        AssignedReports: (prev.AssignedReports || []).filter(report => 
          !reportIds.includes(report.reportID || report.ReportID)
        )
      } : null);
      
      // Refresh data
      loadUsers();
      onRefresh?.();
    } catch (err) {
      showMessage?.(`Failed to remove reports: ${err.message}`, 'error');
    }
  };

  // Use the more detailed users data if available, otherwise fall back to subscription data
  const displayUsers = usersData?.users || usersData || subscriptionData?.users || [];
  // Determine subscription creator/original admin to prevent deleting them
  const originalAdminId = subscriptionData?.createdByAdminID || subscriptionData?.CreatedByAdminID ||
                          subscriptionData?.ownerAdminID || subscriptionData?.OwnerAdminID ||
                          subscriptionData?.createdBy || subscriptionData?.CreatedBy || null;
  const originalAdminEmail = subscriptionData?.createdByEmail || subscriptionData?.CreatedByEmail ||
                             subscriptionData?.ownerAdminEmail || subscriptionData?.OwnerAdminEmail ||
                             subscriptionData?.createdByUser || subscriptionData?.CreatedByUser ||
                             subscriptionData?.BeneficiaryEmail || subscriptionData?.beneficiaryEmail || null;
  // Get list of all admins to prevent deleting users who are also admins
  const adminList = subscriptionData?.admins || [];
  const filteredUsers = (displayUsers || []).filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const email = (u.email || u.Email || '').toLowerCase();
    const name = (u.fullName || u.FullName || '').toLowerCase();
    return email.includes(q) || name.includes(q);
  });
  const licenseInfo = getLicenseInfo();
  
  // Get status color based on license usage
  const licenseStatusColor = getLicenseStatusColor(licenseInfo.active, licenseInfo.total);
  
  // Get billing model description
  const billingModelDesc = getBillingModelDescription(licenseInfo.billingModel || 'PerUser');

  // Get available reports from subscription data
  const availableReports = subscriptionData?.workspaces?.reduce((reports, workspace) => {
    return [...reports, ...(workspace.reports || [])];
  }, []) || [];

  if (!subscriptionData) {
    return null;
  }

  // Check if subscription is suspended
  const isSuspended = subscriptionData.saasSubscriptionStatus === 'Suspended' || 
                      subscriptionData.SaasSubscriptionStatus === 'Suspended';

  return (
    <div className="section">
      {/* Subscription Suspended Warning */}
      {isSuspended && (
        <div style={{ 
          background: '#fff3cd', 
          border: '2px solid #ffc107', 
          padding: '1.5rem', 
          marginBottom: '1.5rem',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16" style={{ color: '#856404', flexShrink: 0 }}>
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>Subscription Suspended</h3>
            <p style={{ margin: 0, color: '#856404' }}>
              Your subscription is currently suspended. All management functions are disabled and users will be unable to access their reports until this is resolved. 
              Please contact Microsoft support to resolve billing or payment issues.
            </p>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>User Management</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users by name or email"
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '260px', fontSize: '14px', height: '38px', boxSizing: 'border-box' }}
            disabled={isSuspended}
          />
          <button 
            className="btn btn-primary" 
            onClick={() => setShowAddUserModal(true)}
            disabled={loading || isSuspended}
            style={{ height: '38px', padding: '0.5rem 0.75rem', boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}
            title={isSuspended ? 'Disabled - Subscription Suspended' : 'Add User'}
          >
            + Add User
          </button>
        </div>
      </div>

      {/* User License Summary */}
      <div className="license-summary" style={{ 
        background: '#e8f4fd', 
        padding: '1rem', 
        marginBottom: '1rem', 
        border: '1px solid #bee5eb',
        borderRadius: '8px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>User License Summary</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <strong>Purchased:</strong>{' '}
            <span style={{ color: '#1B394B' }}>{formatLicenseCount(licenseInfo.purchased)}</span>
          </div>
          <div>
            <strong>Active:</strong>{' '}
            <span style={{ color: '#1B394B' }}>{licenseInfo.active}</span>
          </div>
          <div>
            <strong>Available:</strong>{' '}
            <span style={{ color: '#1B394B' }}>
              {licenseInfo.total >= 999999 ? 'Unlimited' : (licenseInfo.available > 0 ? licenseInfo.available : '0 (At Limit)')}
            </span>
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="alert alert-error" style={{ background: '#fee', border: '1px solid #fcc', padding: '1rem', marginBottom: '1rem' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Users table */}
      <div className="users-list">
        <h3>Current Users ({filteredUsers.length})</h3>
        {displayUsers.length === 0 ? (
          <p>No users found.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>Full Name</th>
                  <th style={{ width: '30%' }}>Email</th>
                  <th style={{ width: '15%' }}>License</th>
                  <th style={{ width: '15%' }}>Reports</th>
                  <th style={{ width: '15%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                      No users match your search.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const userEmail = user.email || user.Email;
                    const userName = user.fullName || user.FullName || 'No Name Provided';
                    const userId = user.userID || user.UserID;
                    const hasLicense = user.hasActiveLicense || user.HasActiveLicense;
                    const assignedReports = user.assignedReports || user.AssignedReports || [];
                    
                    return (
                      <tr key={userId}>
                        <td style={{ fontWeight: '600' }}>{userName}</td>
                        <td>{userEmail}</td>
                        <td style={{ color: hasLicense ? '#28a745' : '#6c757d' }}>
                          {hasLicense ? 'Active' : 'Inactive'}
                        </td>
                        <td>{assignedReports.length}</td>
                        <td>
                          <button 
                            className="btn btn-primary"
                            onClick={() => handleOpenUserPopup(user)}
                            disabled={loading || isSuspended}
                            style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                            title={isSuspended ? 'Disabled - Subscription Suspended' : 'Manage User'}
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Loading indicator */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <div className="loading" style={{ display: 'inline-block' }}></div>
          <span style={{ marginLeft: '0.5rem' }}>Processing...</span>
        </div>
      )}

      {/* User Management Popup */}
      {showUserPopup && selectedUser && (
        <UserManagementPopup
          user={selectedUser}
          availableReports={availableReports}
          onClose={handleCloseUserPopup}
          onUpdateLicense={handleUpdateLicense}
          onDeleteUser={handleDeleteUser}
          onAssignReports={handleAssignReports}
          onRemoveReports={handleRemoveReports}
          licenseInfo={licenseInfo}
          loading={loading}
          isSuspended={isSuspended}
          originalAdminId={originalAdminId}
          originalAdminEmail={originalAdminEmail}
          adminList={adminList}
        />
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <AddUserModal
          onClose={() => setShowAddUserModal(false)}
          onAddUser={handleAddUser}
          loading={loading}
        />
      )}

      {/* User Activation Wizard - shown after user creation */}
      {showActivationWizard && newlyCreatedUser && (
        <UserActivationWizard
          user={newlyCreatedUser}
          availableReports={availableReports}
          licenseInfo={licenseInfo}
          onActivateAndAssign={handleActivateAndAssign}
          onSkip={handleSkipActivation}
          loading={loading}
        />
      )}
    </div>
  );
};

export default UserManagement;